import { useEffect, useState } from 'react';

import { usePermission } from '../../../../auth/AuthContext';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { todayCalendarDate } from '../../../../components/calendar/monthGrid';
import { DateField } from '../../../../components/inputs/DateField';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { eligibilitiesApi } from '../../eligibilities/eligibilitiesApi';
import type { EligibilityOption } from '../../eligibilities/eligibilityTypes';
import { personnelApi } from '../personnelApi';
import type { PersonnelEligibility } from '../personnelTypes';
import { sampleEligibility } from '../utils/sampleEligibility';

/**
 * Records or corrects one earned eligibility — CS Form 212, section IV.
 *
 * **The chosen eligibility decides what the form asks.** CS Form 212 says to give a licence
 * number and its date of validity where the eligibility entails one (RA 1080), and which
 * eligibilities those are is the catalog's answer — `EntailsLicense` on the option. So the
 * licence fields are not drawn at all until a licence-bearing eligibility is chosen, and they
 * are required once it is. A control that can never apply is noise; one the form quietly
 * ignores is worse.
 */
export function EditEligibilityModal({
  personnelId,
  editing,
  onClose,
  onSaved,
}: {
  personnelId: string;
  /** Null adds one. */
  editing: PersonnelEligibility | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  /*
    The catalog's OWN create key, not this form's update key. Adding a word every future 201
    file can be filed under is a different grant from recording that one employee earned one —
    so the Add row is withheld from a reader without it rather than offered and 403'd.
  */
  const canCreateEligibility = usePermission('eligibilities.create');

  const [options, setOptions] = useState<EligibilityOption[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(true);

  const [eligibilityId, setEligibilityId] = useState<string | null>(editing?.eligibilityId ?? null);
  const [rating, setRating] = useState(
    editing?.rating === null || editing?.rating === undefined ? '' : String(editing.rating),
  );
  const [dateOfExamination, setDateOfExamination] = useState(editing?.dateOfExamination ?? '');
  const [placeOfExamination, setPlaceOfExamination] = useState(editing?.placeOfExamination ?? '');
  const [licenseNumber, setLicenseNumber] = useState(editing?.licenseNumber ?? '');
  const [licenseValidUntil, setLicenseValidUntil] = useState(editing?.licenseValidUntil ?? '');

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    eligibilitiesApi
      .options(controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setOptions(next);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setError(
          caught instanceof ApiError
            ? caught
            : new ApiError('Could not load the eligibilities.', 0),
        );
        // No focus call: a failed picker load names no field.
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingOptions(false);
      });

    return () => controller.abort();
  }, []);

  /*
    Derived during render, never held in state: it is a function of the chosen eligibility, and
    storing it would leave the licence fields drawn for one paint after the reader changes the
    picker — the same rule the code suggester follows.
  */
  const chosen = options.find((o) => o.id === eligibilityId) ?? null;

  // Falls back to what the record already said, so the fields do not vanish while the options
  // are still in flight on an edit.
  const entailsLicense = chosen?.entailsLicense ?? editing?.entailsLicense ?? false;

  const ratingValue = rating.trim() === '' ? null : Number(rating);
  const ratingIsNumber = ratingValue === null || Number.isFinite(ratingValue);
  const ratingInRange = ratingValue === null || (ratingValue >= 0 && ratingValue <= 100);

  const licenceNumberMissing = entailsLicense && licenseNumber.trim() === '';
  const expiryBeforeExam =
    licenseValidUntil !== '' && dateOfExamination !== '' && licenseValidUntil < dateOfExamination;

  const valid =
    eligibilityId !== null &&
    dateOfExamination.length > 0 &&
    ratingIsNumber &&
    ratingInRange &&
    !licenceNumberMissing &&
    !expiryBeforeExam;

  const canSave = valid && confirmed && !saving;

  /**
   * Adds an eligibility from inside the picker, and selects it.
   *
   * The picker is over a FOREIGN KEY, so committing the typed text — what a bare `allowCustom`
   * does — would put a label where an id belongs. Writing the catalog row first and selecting
   * the id it comes back with is the only version that saves.
   */
  async function createEligibility(raw: string) {
    const name = raw.trim();
    if (!name) return;

    try {
      const created = await eligibilitiesApi.create({
        name,
        description: null,

        /*
          A 201 file has no business deciding whether an eligibility entails a licence: that is
          a property of the ELIGIBILITY rather than of the person holding it, and an
          administrator answers it in the catalog's own modal. A new row therefore arrives
          carrying none, and the licence block stays undrawn until it says otherwise.
        */
        entailsLicense: false,
      });

      setOptions((current) =>
        current.some((o) => o.id === created.id)
          ? current
          : [
              ...current,
              { id: created.id, name: created.name, entailsLicense: created.entailsLicense },
            ],
      );

      setEligibilityId(created.id);
    } catch (caught) {
      /*
        Caught here rather than left to reject. The panel's Add row fires this with no catch of
        its own, so an unhandled rejection would leave the reader looking at a picker that did
        nothing and no sentence saying why — a refused create, a name another reader took in
        the same moment, a dropped connection. The banner is where this form reports.

        No focus call: the failure is the catalog's, and it names no field on this form.
      */
      setError(
        caught instanceof ApiError ? caught : new ApiError('Could not add that eligibility.', 0),
      );
    }
  }

  /*
    Fills the form from the catalog this modal already loaded, so the eligibility is a row that
    exists and the licence fields follow whether that row entails one.

    Disabled until the options arrive — a generator that ran against an empty catalog would
    leave the one required foreign key null and the form still refusing to save, which reads
    as the button being broken.
  */
  function generate() {
    const sample = sampleEligibility(options);

    setEligibilityId(sample.eligibilityId);
    setRating(sample.rating);
    setDateOfExamination(sample.dateOfExamination);
    setPlaceOfExamination(sample.placeOfExamination);
    setLicenseNumber(sample.licenseNumber);
    setLicenseValidUntil(sample.licenseValidUntil);
  }

  async function save() {
    if (!canSave || eligibilityId === null) return;

    setSaving(true);
    setError(null);

    const request = {
      eligibilityId,
      rating: ratingValue,
      dateOfExamination,
      placeOfExamination: placeOfExamination.trim() || null,

      // Cleared rather than carried when the eligibility entails no licence — otherwise
      // re-pointing a row from a board eligibility leaves a number behind on one without.
      licenseNumber: entailsLicense ? licenseNumber.trim() || null : null,
      licenseValidUntil: entailsLicense ? licenseValidUntil || null : null,
    };

    try {
      if (editing) await personnelApi.updateEligibility(personnelId, editing.id, request);
      else await personnelApi.addEligibility(personnelId, request);

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not save this record.', 0);

      setError(failure);
      focusValidationError(failure, {
        idPrefix: 'eligibility',
        // The server names the catalog field; the control that chooses it is the picker.
        fieldIds: { eligibilityId: 'eligibility-eligibility' },
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? 'Edit eligibility' : 'Add eligibility'}
      size="2xl"
      id="eligibility-modal"
      /*
        Autogenerate, create AND edit — see CLAUDE.md. This modal used to omit it, with a
        comment arguing that a generator "would have to invent a plausible score for a real
        eligibility, which is test data that reads as a fact about a person".

        That argument is true of every generator in this product and is not a reason to skip
        one: a generated personnel record invents a name, a birth date and a contact number,
        and the rule it follows is to make the values obviously synthetic rather than to
        withhold the button. The foreign key is a real row from the catalog the form already
        loaded, which is what the rule asks for; the score is a number, and `Test City` is not
        a place anybody lives.

        Recorded because the omission was argued for rather than overlooked, and the argument
        is the shape of the mistake: a rule with a defensible-sounding exception written
        beside it is still a rule not followed.
      */
      autogenerate={{ onGenerate: generate, disabled: saving || loadingOptions }}
      confirm={{
        id: 'eligibility-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="eligibility-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="eligibility-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <Select
          id="eligibility-eligibility"
          label="Eligibility"
          required
          remote
          loading={loadingOptions}
          /*
            The sentence about adding one is dropped with the affordance, rather than standing
            there promising an Add row a reader without the key will never be shown.
          */
          description={
            canCreateEligibility
              ? 'From the eligibility catalog. Whether a licence is asked for follows from the one chosen. Type a name that is not listed to add it.'
              : 'From the eligibility catalog. Whether a licence is asked for follows from the one chosen.'
          }
          /*
            `allowCustom` with `onCreateOption`: an unmatched query offers Add "…" and the
            handler writes the catalog row under its own key before selecting it. Withheld from
            a reader without that key — hide, don't disable.
          */
          allowCustom={canCreateEligibility}
          onCreateOption={canCreateEligibility ? createEligibility : undefined}
          value={eligibilityId}
          onChange={(next) => setEligibilityId(next || null)}
          options={options.map((o) => ({ value: o.id, label: o.name }))}
          error={eligibilityId ? error?.fieldError('eligibilityId') : 'Choose an eligibility.'}
          disabled={saving}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="eligibility-rating"
            label="Rating"
            type="number"
            min={0}
            max={100}
            step="0.01"
            /*
              Optional, and the description says why rather than leaving a required-looking
              field blank: the form's own examples — Honor Graduate, Veteran Preference,
              Barangay Health Worker — are conferred rather than examined for.
            */
            description="Leave blank where the eligibility was conferred rather than examined for."
            value={rating}
            onChange={(e) => setRating(e.target.value)}
            error={
              !ratingIsNumber
                ? 'Enter a rating.'
                : !ratingInRange
                  ? 'Enter a rating between 0 and 100.'
                  : error?.fieldError('rating')
            }
            disabled={saving}
          />

          <DateField
            id="eligibility-date-of-examination"
            label="Date of examination or conferment"
            required
            max={todayCalendarDate()}
            value={dateOfExamination}
            onChange={setDateOfExamination}
            error={
              dateOfExamination
                ? error?.fieldError('dateOfExamination')
                : 'Choose the date of examination or conferment.'
            }
            disabled={saving}
          />
        </div>

        <TextField
          id="eligibility-place-of-examination"
          label="Place of examination or conferment"
          value={placeOfExamination}
          onChange={(e) => setPlaceOfExamination(e.target.value)}
          error={error?.fieldError('placeOfExamination')}
          disabled={saving}
        />

        {entailsLicense ? (
          <div className="flex flex-col gap-4">
            <h3 className="text-sm text-slate-700">Licence</h3>

            <p className="text-xs text-slate-500">
              {chosen?.name ?? 'This eligibility'} entails a licence, so CS Form 212 asks for its
              number and the date it is valid until.
            </p>

            <div className="grid gap-4 sm:grid-cols-2">
              <TextField
                id="eligibility-license-number"
                label="Licence number"
                required
                value={licenseNumber}
                onChange={(e) => setLicenseNumber(e.target.value)}
                error={
                  licenceNumberMissing
                    ? 'Enter the licence number.'
                    : error?.fieldError('licenseNumber')
                }
                disabled={saving}
              />

              <DateField
                id="eligibility-license-valid-until"
                label="Valid until"
                /*
                  No `max`: unlike every other date on this form it points FORWARD while the
                  licence is current, so capping it at today would refuse every valid licence.
                */
                description="The licence's date of validity. Blank if it does not expire."
                value={licenseValidUntil}
                onChange={setLicenseValidUntil}
                error={
                  expiryBeforeExam
                    ? 'Choose a date on or after the examination.'
                    : error?.fieldError('licenseValidUntil')
                }
                disabled={saving}
              />
            </div>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
